import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query, where, type QueryConstraint } from 'firebase/firestore';
import { db } from '../firebase';
import { useCollection } from './useCollection';
import { hasCouncilFullRead } from '../lib/visibility';
import { tenantWhere, useActiveTenantId } from '../lib/tenant';
import type {
  DistrictSummary,
  ISODateString,
  MembershipSnapshot,
  Unit,
  UnitAdvancementSnapshot,
  UnitCommissionerAssignment,
  UnitConnection,
  UnitImport,
  UnitLeader,
  UnitLeaderSnapshot,
  UnitSyt,
  UserProfile,
} from '../types';

/** Units for this user — admin sees all, everyone else their own district
 *  (same shape as tasks/initiatives/contacts). */
export function useVisibleUnits(profile: UserProfile) {
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const activeTenantId = useActiveTenantId();
  const constraints: QueryConstraint[] = useMemo(
    () => [tenantWhere(), ...(unscoped || !profile.districtId ? [] : [where('districtId', '==', profile.districtId)])],
    [unscoped, profile.districtId, activeTenantId],
  );
  const enabled = unscoped || !!profile.districtId;
  return useCollection<Unit>('units', constraints, enabled);
}

/** Commissioner-to-unit links this user may read — the same scope as the units themselves (admin and council-wide
 *  readers see all; everyone else their own district). References only: names come from the Person-label lookup,
 *  and no contact detail lives on these documents. */
export function useVisibleUnitCommissioners(profile: UserProfile) {
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const activeTenantId = useActiveTenantId();
  const constraints: QueryConstraint[] = useMemo(
    () => [tenantWhere(), ...(unscoped || !profile.districtId ? [] : [where('districtId', '==', profile.districtId)])],
    [unscoped, profile.districtId, activeTenantId],
  );
  const enabled = unscoped || !!profile.districtId;
  return useCollection<UnitCommissionerAssignment>('unitCommissionerAssignments', constraints, enabled);
}

/** One-shot fetch of a single unit's advancement history — for the drawer
 *  sparkline only. Scoped `where('unitId','==')` query, not a collection scan;
 *  runs only while a unit drawer is open. The *current* advancement state is
 *  denormalized on the unit doc (`unit.latestAdvancement`) — screens read that. */
export function useUnitAdvancementHistory(unitId: string | null) {
  const [data, setData] = useState<UnitAdvancementSnapshot[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!unitId) {
      setData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getDocs(
      query(
        collection(db, 'unitAdvancementSnapshots'),
        where('unitId', '==', unitId),
        tenantWhere(),
        orderBy('reportDate', 'asc'),
      ),
    )
      .then((snap) => {
        if (cancelled) return;
        setData(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }) as UnitAdvancementSnapshot),
        );
      })
      .catch(() => {
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  return { data, loading };
}

/** One-shot fetch of a single unit's commissioner connection history — for
 *  the Commissioners page detail panel. Scoped by both unitId and
 *  districtId (not unitId alone) because firestore.rules' read check
 *  depends on districtId — a query only Firestore can prove satisfies the
 *  rule for a plain district-scoped viewer, not just admin/council-wide. */
export function useUnitConnections(unitId: string | null, districtId: string | null) {
  const [data, setData] = useState<UnitConnection[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!unitId || !districtId) {
      setData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getDocs(
      query(
        collection(db, 'unitConnections'),
        where('unitId', '==', unitId),
        where('districtId', '==', districtId),
        tenantWhere(),
        orderBy('connectionDate', 'desc'),
      ),
    )
      .then((snap) => {
        if (cancelled) return;
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as UnitConnection));
      })
      .catch(() => {
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [unitId, districtId]);

  return { data, loading };
}

/** Import metadata — the "last imported" line. Readable by any profile. */
export function useUnitImports() {
  return useCollection<UnitImport>('unitImports', [orderBy('importedAt', 'desc'), tenantWhere()]);
}

/** Trained-leader records — admin sees all (incl. council-scoped); everyone
 *  else sees their own district's unit- and district-scoped leaders. */
export function useVisibleUnitLeaders(profile: UserProfile) {
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const activeTenantId = useActiveTenantId();
  const constraints: QueryConstraint[] = useMemo(
    () => [tenantWhere(), ...(unscoped || !profile.districtId ? [] : [where('districtId', '==', profile.districtId)])],
    [unscoped, profile.districtId, activeTenantId],
  );
  const enabled = unscoped || !!profile.districtId;
  return useCollection<UnitLeader>('unitLeaders', constraints, enabled);
}

/** Every trained-leader record — for the admin-only "find a leader" search on
 *  Add User. Rules only let admins read the whole collection, so `enabled`
 *  must gate this to admin contexts. */
export function useAllUnitLeaders(enabled: boolean) {
  return useCollection<UnitLeader>('unitLeaders', [tenantWhere()], enabled);
}

/** Precomputed per-district dashboard aggregates. Non-admins see their own;
 *  admins see all 4. The Dashboard uses these instead of scanning
 *  unitLeaders / unitSyt for its totals. */
export function useDistrictSummaries(profile: UserProfile) {
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const activeTenantId = useActiveTenantId();
  const constraints: QueryConstraint[] = useMemo(
    () => [tenantWhere(), ...(unscoped || !profile.districtId ? [] : [where('districtId', '==', profile.districtId)])],
    [unscoped, profile.districtId, activeTenantId],
  );
  const enabled = unscoped || !!profile.districtId;
  return useCollection<DistrictSummary>('districtSummaries', constraints, enabled);
}

/** Monthly membership snapshots — same district scoping as units. Drives the
 *  dashboard membership trend line. */
export function useVisibleMembership(profile: UserProfile) {
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const activeTenantId = useActiveTenantId();
  const constraints: QueryConstraint[] = useMemo(
    () => [tenantWhere(), ...(unscoped || !profile.districtId ? [] : [where('districtId', '==', profile.districtId)])],
    [unscoped, profile.districtId, activeTenantId],
  );
  const enabled = unscoped || !!profile.districtId;
  return useCollection<MembershipSnapshot>('membershipSnapshots', constraints, enabled);
}

// ---- Leadership snapshot history (read foundation) -----------------------
// Minimum query support for future Leadership Sustainability analytics —
// see docs/product-module-architecture.md's Leadership History section.
// Deliberately no big new screen here; these exist so that work has
// somewhere to start from.

/** Dates that have a TRUSTED Trained Leaders history snapshot, newest
 *  first. Reads unitImports (one small doc per import) rather than scanning
 *  the potentially much larger unitLeaderSnapshots collection — the same
 *  (tenantId, reportType, reportDate) index importTrainedLeaders() itself
 *  uses to find its own row-count comparison baseline. An import whose
 *  history was skipped (evaluateSnapshotTrust() rejected it — see
 *  lib/units.ts) is filtered out here, not just left for the caller to
 *  notice. */
export async function listUnitLeaderSnapshotDates(): Promise<ISODateString[]> {
  const snap = await getDocs(
    query(
      collection(db, 'unitImports'),
      where('reportType', '==', 'trainedLeaders'),
      tenantWhere(),
      orderBy('reportDate', 'desc'),
    ),
  );
  return snap.docs
    .map((d) => d.data() as UnitImport)
    .filter((i) => i.snapshotCreated)
    .map((i) => i.reportDate);
}

/** One unit's leadership history — a single historical date (`date`), a
 *  date range (`from`/`to`, inclusive), or (with neither) its full
 *  recorded history, oldest first. Mirrors useUnitAdvancementHistory /
 *  useUnitConnections above: a one-shot fetch for a detail panel, not a
 *  live subscription — history doesn't change once written except by a
 *  same-date correction, which a re-open of the panel will pick up. */
export function useUnitLeaderSnapshots(
  unitId: string | null,
  range?: { date?: ISODateString; from?: ISODateString; to?: ISODateString },
) {
  const [data, setData] = useState<UnitLeaderSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const date = range?.date;
  const from = range?.from;
  const to = range?.to;

  useEffect(() => {
    if (!unitId) {
      setData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const constraints = [
      where('unitId', '==', unitId),
      tenantWhere(),
      ...(date ? [where('snapshotDate', '==', date)] : []),
      ...(from ? [where('snapshotDate', '>=', from)] : []),
      ...(to ? [where('snapshotDate', '<=', to)] : []),
      orderBy('snapshotDate', 'asc'),
    ];
    getDocs(query(collection(db, 'unitLeaderSnapshots'), ...constraints))
      .then((snap) => {
        if (cancelled) return;
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as UnitLeaderSnapshot));
      })
      .catch(() => {
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [unitId, date, from, to]);

  return { data, loading };
}

/** The bulk equivalent of useUnitLeaderSnapshots, for the council/district
 *  Leadership Attention view (Leadership Sustainability V1) — that screen
 *  needs a leadership-history delta for EVERY visible unit at once, which
 *  would mean one query per unit if it reused the per-unit hook above.
 *  Instead this fetches just the two most recent TRUSTED snapshot dates'
 *  worth of docs, across every unit, in two queries total. Explicitly
 *  district-filtered (unlike useUnitLeaderSnapshots, which is safe to
 *  leave unfiltered because it's already scoped to one specific unitId —
 *  a query spanning many units/districts needs the same explicit
 *  district-scoping every other bulk hook in this file uses, or
 *  firestore.rules will reject the whole query for a non-admin/
 *  non-council-wide caller the moment it would return another district's
 *  doc). One-shot fetch, not live — matches the drawer-history hooks
 *  above, not the live useCollection() hooks. */
export function useRecentUnitLeaderSnapshots(profile: UserProfile) {
  const [dates, setDates] = useState<ISODateString[]>([]);
  const [byDate, setByDate] = useState<Map<ISODateString, UnitLeaderSnapshot[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const activeTenantId = useActiveTenantId();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const allDates = await listUnitLeaderSnapshotDates(); // newest first
      const recent = allDates.slice(0, 2).reverse(); // oldest, then newest
      if (recent.length === 0) {
        if (!cancelled) {
          setDates([]);
          setByDate(new Map());
          setLoading(false);
        }
        return;
      }
      const districtConstraint =
        unscoped || !profile.districtId ? [] : [where('districtId', '==', profile.districtId)];
      const results = await Promise.all(
        recent.map((d) =>
          getDocs(
            query(collection(db, 'unitLeaderSnapshots'), where('snapshotDate', '==', d), tenantWhere(), ...districtConstraint),
          ),
        ),
      );
      if (cancelled) return;
      const map = new Map<ISODateString, UnitLeaderSnapshot[]>();
      recent.forEach((d, i) => {
        map.set(
          d,
          results[i].docs.map((doc) => ({ id: doc.id, ...doc.data() }) as UnitLeaderSnapshot),
        );
      });
      setDates(recent);
      setByDate(map);
      setLoading(false);
    })().catch(() => {
      if (!cancelled) {
        setDates([]);
        setByDate(new Map());
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unscoped, profile.districtId, activeTenantId]);

  return { dates, byDate, loading };
}

/** Safeguarding Youth Training records — admin sees all; everyone else sees
 *  people registered in their own district (`districtIds` array-contains).
 *  `needed` (default true, so existing callers are unaffected) lets a caller
 *  defer the fetch until it's genuinely required — e.g. Unit Health only
 *  needs this collection for its Leaders/Attention tabs or an open unit
 *  drawer, not merely because the page is mounted on Units/Charters. */
export function useVisibleSyt(profile: UserProfile, needed = true) {
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const activeTenantId = useActiveTenantId();
  const constraints: QueryConstraint[] = useMemo(
    () => [
      tenantWhere(),
      ...(unscoped || !profile.districtId ? [] : [where('districtIds', 'array-contains', profile.districtId)]),
    ],
    [unscoped, profile.districtId, activeTenantId],
  );
  const enabled = needed && (unscoped || !!profile.districtId);
  return useCollection<UnitSyt>('unitSyt', constraints, enabled);
}
